import { noop } from '@proc7ts/primitives';
import { ZOptionError } from './option-error.js';
import { ZOptionLocation } from './option-location.js';
/**
 * @internal
 */
export class ZOptionImpl {
    optionsMeta;
    argIndex;
    #args;
    #head;
    #name;
    #key;
    #values;
    #recognizedUpto;
    #actions = [];
    #deferred;
    #allDeferred = [];
    #reason;
    #finalReason;
    recognized;
    #whenRecognized = noop;
    constructor(optionsMeta, args, argIndex) {
        this.optionsMeta = optionsMeta;
        this.argIndex = argIndex;
        this.#args = args;
        this.#head = args.slice(0, argIndex);
    }
    get args() {
        return this.#args;
    }
    get name() {
        return this.#name;
    }
    get key() {
        return this.#key;
    }
    get tail() {
        return this.args.slice(this.argIndex);
    }
    setInput(input) {
        const { name, key = name, values = [], tail = [] } = input;
        this.#name = name;
        this.#key = key;
        this.#values = values;
        return (this.#args = [...this.#head, name, ...values, ...tail]);
    }
    async read(option, reader) {
        this.#actions = [];
        this.#reason = undefined;
        if (!this.recognized) {
            this.#recognizedUpto = -1;
            this.#deferred = undefined;
        }
        await reader.read(option);
        if (this.#deferred) {
            this.#allDeferred.push(this.#deferred);
            if (this.#finalReason == null) {
                this.#finalReason = this.#reason;
            }
        }
        else {
            const actions = this.#actions;
            if (actions.length) {
                this.whenRecognized(() => {
                    for (const action of actions) {
                        action();
                    }
                });
            }
            if (!this.recognized && this.#recognizedUpto >= 0) {
                this.recognized = this.args.slice(this.argIndex + 1, this.#recognizedUpto);
            }
        }
    }
    async done(option) {
        if (this.recognized) {
            this.#actions = [];
            for (const deferred of this.#allDeferred) {
                await deferred(option);
            }
            this.#whenRecognized(option);
            // Perform actions registered by deferred callbacks
            for (const action of this.#actions) {
                action();
            }
        }
        else if (this.#finalReason != null) {
            throw this.#finalReason;
        }
        else {
            throw new ZOptionError(this.optionLocation(), `Unrecognized command line option: "${this.name}"`);
        }
        return this.#recognizedUpto;
    }
    values(rest, max) {
        if (max != null && max < 0) {
            max = 0;
        }
        if (this.recognized) {
            return max != null && max < this.recognized.length
                ? this.recognized.slice(0, max)
                : this.recognized;
        }
        const fromIndex = this.argIndex + 1;
        const toIndex = max != null
            ? fromIndex + (rest ? max : Math.min(max, this.#values.length))
            : rest
                ? this.#args.length
                : fromIndex + this.#values.length;
        const result = this.args.slice(fromIndex, toIndex);
        this.#recognize(fromIndex + result.length);
        return result;
    }
    recognize(action) {
        if (this.#recognizedUpto < 0) {
            this.#recognize(this.argIndex + 1);
        }
        if (action) {
            this.#actions.push(action);
        }
    }
    #recognize(upto) {
        this.#recognizedUpto = upto;
        this.#deferred = undefined;
        this.#reason = undefined;
    }
    defer(whenRecognized = noop) {
        this.#deferred = whenRecognized;
    }
    unrecognize(reason) {
        if (this.recognized) {
            return;
        }
        if (!this.#deferred) {
            this.#deferred = noop;
        }
        if (reason != null) {
            this.#reason = reason;
        }
        this.#recognizedUpto = -1;
        this.#actions.length = 0;
    }
    whenRecognized(receiver) {
        const prevReceiver = this.#whenRecognized;
        this.#whenRecognized = values => {
            prevReceiver(values);
            receiver(values);
        };
    }
    optionLocation(init = {}) {
        return ZOptionLocation.by({
            ...init,
            index: init.index || this.argIndex,
            args: this.args,
        });
    }
}
//# sourceMappingURL=option.impl.js.map